import {App, Plugin, computed} from 'vue'
import Keycloak from 'keycloak-js';
import AuthenticatedUser from '@/entities/authenticated-user'
import {AuthenticationOptions, KeycloakConfig} from '@/config/keycloak'
import AxiosStatic from 'axios'
import AuthenticatedAxios from '@/config/api-config'
import parse_jwt from '@/utils/parse-jwt'
import getLanguageFromDomain, {supportedLanguages} from '@/utils/language'
import {TranslationsService} from "@/services/translations.service";
import {useProfileStore} from '@/stores/profile'
import {User} from '@/entities/user'
import i18n from '@/plugins/i18n'

const translationsService = new TranslationsService();
export const authKey = Symbol('auth');

declare module 'vue' {
  interface ComponentCustomOptions {
    authentication?: AuthenticationOptions
  }

  interface ComponentCustomProperties {
    $keycloak: Keycloak
    user: AuthenticatedUser
    userHasRole: (role: string) => boolean
  }
}

const fetchPermissionsToken = async function (this: Keycloak): Promise<object | undefined> {
  if (!this.authenticated || !this.token) {
    this.login({locale: 'en'})
    return
  }

  const params = new URLSearchParams();
  params.append('grant_type', 'urn:ietf:params:oauth:grant-type:uma-ticket')
  params.append('audience', KeycloakConfig.backendClientId)
  params.append('client_id', KeycloakConfig.clientId)

  const permissionTokenResult = await AxiosStatic.post(`${KeycloakConfig.url}realms/${KeycloakConfig.realm}/protocol/openid-connect/token`,
    params,
    {
      headers: {
        'Authorization': 'Bearer ' + this.token
      }
    })

  this.permissions_token = permissionTokenResult.data.access_token
  this.permissions_token_parsed = parse_jwt(this.permissions_token)

  return this.permissions_token_parsed
}

Keycloak.prototype.fetchPermissionsToken = fetchPermissionsToken
Keycloak.prototype.getPermissionsToken = async function (this: Keycloak, forceUpdate = false): Promise<string | undefined> {
  if (forceUpdate || !this.authenticated || !this.permissions_token || this.isTokenExpired()) {
    await this.fetchPermissionsToken().catch(() => {
      this.logout()
    })
  }

  if (this.permissions_token) {
    return this.permissions_token
  }

  return undefined
}

Keycloak.prototype.fetchPassphrase = async function (this: Keycloak): Promise<string | undefined> {
  if (!this.authenticated || !this.token) {
    this.login({locale: 'en'})
    return
  }

  if (this.isTokenExpired()) {
    console.warn('token expired, triggering re-login')
    this.login({locale: 'en'})
    return
  }

  return AxiosStatic.put(`${KeycloakConfig.backendApi}/users/passphrase`,
    null, {
      headers: {
        'Authorization': 'Bearer ' + this.token
      }
    })
}

/**
 * Set the frontend language to the locale that we get from Keycloak if available
 */
Keycloak.prototype.setLocale = function (): Promise<any> {
  const locale =
    this.permissions_token_parsed?.locale && supportedLanguages.includes(this.permissions_token_parsed.locale)
      ? this.permissions_token_parsed.locale
      : getLanguageFromDomain()
  i18n.global.locale.value = locale
  return translationsService.postCulture(locale)
}

Keycloak.prototype.getPassphrase = async function (this: Keycloak): Promise<string | undefined> {

  if (!this.permissions_token) {
    await this.getPermissionsToken()
  }
  return this.permissions_token_parsed?.passphrase ?? undefined
}

const CegatKeycloak = new Keycloak(KeycloakConfig)

CegatKeycloak.onAuthRefreshSuccess = CegatKeycloak.onAuthSuccess = function () {
  this.fetchPermissionsToken()
    .then(async () => {
      this.setLocale()

      const profileStore = useProfileStore()
      profileStore.setProfile({
        userId: this.tokenParsed?.sub || '',
        firstName: this.tokenParsed?.given_name || '',
        lastName: this.tokenParsed?.family_name || '',
        email: this.tokenParsed?.email || '',
      } as Partial<User>)

      if (this.tokenParsed?.sub) {
        await profileStore.fetchProfile(this.tokenParsed.sub)
      }
    })
    .catch(() => {
      this.logout()
    })
}

export function createAuth() {
  return computed(() => {
    const parsed = CegatKeycloak.tokenParsed;
    return parsed ? new AuthenticatedUser(parsed) : null;
  });
}

export const AuthenticationPlugin: Plugin = {
  install(app: App) {
    app.config.globalProperties.$keycloak = CegatKeycloak

    const auth = createAuth();
    app.provide(authKey, auth);
    app.config.globalProperties.$auth = auth;

    AuthenticatedAxios._keycloak = CegatKeycloak
  }
}

// install
export {CegatKeycloak}
